import { HttpErrorResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { AlertController, ModalController, PopoverController } from '@ionic/angular/standalone';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { concatLatestFrom } from '@ngrx/operators';
import { Store } from '@ngrx/store';
import { from, of } from 'rxjs';
import { catchError, concatMap, filter, map, mergeMap, switchMap } from 'rxjs/operators';
import { routePaths } from 'src/app/app.routes';
import { ErrorService } from 'src/app/services/error.service';
import { PatientsService } from 'src/app/services/patients.service';
import { AuditLabel, CommsPanelChildType, SegmentLabelType, SheetLabel, TasksLabel, VisitLabel } from 'src/app/types/patient.types';
import { closeAllAlerts, closeAllModals, closeAllPopovers } from 'src/app/types/rxjs-custom-operator.types';
import { clientErrored } from '../error/error.actions';
import * as patientGraphActions from '../patient-graph/patient-graph.actions';
import { isDesktop, isMobile } from '../platform/platform.selectors';
import { AppState } from '../reducers';
import { selectCurrentRoute } from '../router/router.selectors';
import { hideMigrationFeatures } from '../user-graph/user-graph.selectors';
import * as patientActions from './patient.actions';
import { patientStatus } from './patient.selectors';

@Injectable()
export class PatientEffects {

	constructor(
		private actions$: Actions, private store: Store<AppState>, private alertController: AlertController, private modalController: ModalController,
		private popoverController: PopoverController, private patientService: PatientsService, private errorService: ErrorService
	) {
	}

	subscribePatientRequested$ = createEffect(() =>
		this.actions$.pipe(
			ofType(patientGraphActions.subscribePatientRequested),
			filter(action => action.patientChanged),
			concatLatestFrom(() => [
				this.store.select(isDesktop),
				this.store.select(isMobile),
				this.store.select(selectCurrentRoute),
				this.store.select(hideMigrationFeatures)
			]),
			switchMap(([, isDesktop, isMobile, currentRoute, hideMigrationFeatures]) => {
				const actions = [];

				let segmentLabel: SegmentLabelType;

				switch (currentRoute.url[0].path) {
					case routePaths.inHospitalList:
						segmentLabel = !isMobile ? SheetLabel : TasksLabel; // sheet tab hides on mobile, so show notes
						break;
					case routePaths.service:
						segmentLabel = TasksLabel; // always show tasks when coming from service, tasks tab is never hidden
						break;
					case routePaths.patient:
						segmentLabel = isDesktop ? TasksLabel : VisitLabel; // default to visit, but show notes if on desktop (visit tab is hidden on desktop)
						break;
					default:
						throw new Error(`Unexpected patient route path: ${currentRoute.url[0].path}`);
				}

				actions.push(patientActions.patientSelected({ segmentLabel }));

				if (!isMobile) actions.push(patientActions.commsPanelChildSelected({ childType: hideMigrationFeatures ? CommsPanelChildType.ReferringVet : CommsPanelChildType.CommunicationsLog }));

				return actions;
			})
		)
	);

	unsubscribePatientRequested$ = createEffect(() =>
		this.actions$.pipe(
			ofType(patientGraphActions.unsubscribePatientRequested),
			map(() => patientActions.patientDeselected())
		)
	);

	isDesktopChanged$ = createEffect(() =>
		this.store.select(isDesktop).pipe(
			concatLatestFrom(() => this.store.select(patientStatus)),
			// if a patient is selected and the selected segment is "visit", it gets hidden when switching to desktop, so select the "tasks" segment
			filter(([isDesktop, patientStatus]) => patientStatus.patientSelected && isDesktop && patientStatus.segmentLabel === VisitLabel),
			map(() => patientActions.segmentChanged({ label: TasksLabel }))
		)
	);

	isMobileChangedSheet$ = createEffect(() =>
		this.store.select(isMobile).pipe(
			concatLatestFrom(() => this.store.select(patientStatus)),
			// if a patient is selected and the selected segment is "sheet", it gets hidden when switching to desktop, so select the "visit" segment
			filter(([isMobile, patientStatus]) => patientStatus.patientSelected && isMobile && patientStatus.segmentLabel === SheetLabel),
			map(() => patientActions.segmentChanged({ label: VisitLabel }))
		)
	);

	isMobileChangedAudit$ = createEffect(() =>
		this.store.select(isMobile).pipe(
			concatLatestFrom(() => this.store.select(patientStatus)),
			// if a patient is selected and the selected segment is "audit", it gets hidden when switching to mobile, so select the "visit" segment
			filter(([isMobile, patientStatus]) => patientStatus.patientSelected && isMobile && patientStatus.segmentLabel === AuditLabel),
			map(() => patientActions.segmentChanged({ label: VisitLabel }))
		)
	);

	visitAdded$ = createEffect(() =>
		this.actions$.pipe(
			ofType(patientGraphActions.visitAdded),
			concatMap(() => from(this.alertController.create({
				subHeader: 'New Visit Added',
				message: 'A new visit has been added for this patient. The record will be updated.',
				buttons: ['Ok'],
				backdropDismiss: false
			})).pipe(
				switchMap(alert => from(alert.present()).pipe(map(() => alert))),
				switchMap(alert => from(alert.onDidDismiss()).pipe(
					closeAllModals(this.modalController),
					closeAllPopovers(this.popoverController),
					closeAllAlerts(this.alertController),
					map(() => patientGraphActions.reloadPatientRequested())
				))
			))
		)
	);

	saveProblemsRequested$ = createEffect(() =>
		this.actions$.pipe(
			ofType(patientActions.saveProblemsRequested),
			mergeMap(action => this.patientService.setProblems(action.patientId, action.problems).pipe(
				map(() => patientGraphActions.patientUpdated({ partialPatient: { id: action.patientId, problems: action.problems } })),
				catchError((error: HttpErrorResponse) => of(clientErrored({ toastMessage: 'Save problems failed', errorMessage: this.errorService.getErrorMessage(error) })))
			))
		)
	);

	saveAlertsRequested$ = createEffect(() =>
		this.actions$.pipe(
			ofType(patientActions.saveAlertsRequested),
			mergeMap(action => this.patientService.setAlerts(action.patientId, action.alerts).pipe(
				map(() => patientGraphActions.patientUpdated({ partialPatient: { id: action.patientId, alerts: action.alerts } })),
				catchError((error: HttpErrorResponse) => of(clientErrored({ toastMessage: 'Save alerts failed', errorMessage: this.errorService.getErrorMessage(error) })))
			))
		)
	);
}